import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Input, Modal, Trans } from '@/components'

import { validFileName } from '../fs-operations'
import * as fsOps from '../fs-operations'
import type { FSContext, IFile } from '../types'

export interface RenameDialogProps {
  onCancel: () => void
  onOk: () => void
  file: IFile | undefined
  files: IFile[] | undefined
}

const RenameDialogCore = ({
  file,
  files,
  onCancel,
  onOk,
  domain,
  context,
}: RenameDialogProps & { domain: number; context: FSContext }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState<string>(file?.name || '')

  // Sync file.name to inputValue when file changes
  useEffect(() => {
    if (file?.name) {
      setInputValue(file.name)
    }
  }, [file])

  const isNameOK = useCallback(
    (name: string) => {
      if (files?.some((it: IFile) => it.name == name) || !validFileName(name)) {
        return false
      }
      return true
    },
    [files],
  )

  const _onConfirm = useCallback(() => {
    if (isNameOK(inputValue) && file) {
      const newName = inputValue
      const path = file.path,
        target = file.isDir ? `${file.dirname}${newName}/` : `${file.dirname}${newName}`
      setLoading(true)
      fsOps
        .mv(context, { path, target, domain })
        .then(onOk)
        .catch(() => {})
        .finally(() => {
          setLoading(false)
        })
    }
  }, [file, inputValue, isNameOK, onOk, domain, context])

  return (
    <Modal open={Boolean(file)} onClose={onCancel}>
      <Modal.Header>{t('data.renameModal.title')}</Modal.Header>
      <Modal.Body>
        <Input
          maxLength={30}
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          placeholder={t('data.renameModal.placeholder')}
          onClear={() => {
            setInputValue('')
          }}
          disabled={loading}
          onPressEnter={_onConfirm}
          autoFocus
        />
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onCancel}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button type="secondary" onClick={_onConfirm} disabled={loading || !isNameOK(inputValue)}>
          {loading ? (
            <Trans i18nKey="common.confirmModalOkLoading">Processing...</Trans>
          ) : (
            <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export function useRenameCore(context: FSContext, domain: number) {
  return {
    RenameDialog: useCallback(
      (props: RenameDialogProps) => (
        <RenameDialogCore {...props} domain={domain} context={context} />
      ),
      [domain, context],
    ),
  }
}
